'use strict';

import * as d3 from 'd3';
import { ellipsisText, wrapText } from './utils';
import { X_AXIS_CLASSNAME } from './x-axis';

export const Y_AXIS_CLASSNAME = 'y-axis';
export const Y_AXIS_LABEL_CLASSNAME = 'y-axis-label';

export const getYScale = (range, domain, options = {}) => {
  const scaleType = options.scaleType !== undefined && d3[options.scaleType] ? options.scaleType : 'scaleBand';
  const d3Scale = d3[scaleType]();
  const yScale = d3Scale.range(range).domain(domain);
  yScale.scaleType = scaleType;
  if (options.scaleType !== 'scaleLinear') {
    const padding = options.padding !== undefined ? options.padding : 0.1;
    yScale.padding(padding);
  }
  return yScale;
};

export const getYAxis = (yScale, options = {}) => {
  const yAxis = d3.axisLeft(yScale);
  if (options && options.tickSizeInner) {
    yAxis.tickSizeInner(options.tickSizeInner);
  }
  const tickPadding = (options && options.tickPadding) ? options.tickPadding : 5;
  yAxis.tickPadding(tickPadding);
  if (options && options.tickFormat) {
    yAxis.tickFormat(options.tickFormat);
  }
  if (options.customDomain !== undefined) {
    yAxis.ticks(options.customDomain.length);
    yAxis.tickValues(options.customDomain);
  }
  if (options && options.isRemoveDecimalTick) {
    const currentTicks = yScale.ticks();
    const newTicks = [];
    currentTicks.forEach(function (d) {
      if (d % 1 === 0) {
        newTicks.push(d);
      }
    });
    yAxis.tickValues(newTicks);
    yAxis.tickFormat((d) => { return parseInt(d); });
  }

  return yAxis;
};

export const renderYAxis = ($container, yAxis, options = {}) => {
  const _translate = {
    x: options.translate && options.translate.x ? options.translate.x : 0,
    y: options.translate && options.translate.y ? options.translate.y : 0
  };
  const showBarGroupedLabel = options.showBarGroupedLabel ? options.showBarGroupedLabel : false;
  const labelOffsetX = options.label && options.label.offsetX ? options.label.offsetX : 5;
  const labelHeight = 15;
  const joinedYAxis = $container.selectAll(`.${Y_AXIS_CLASSNAME}`).data([0]);
  const adjustYAxis = $yAxis => {
    if ($yAxis.empty()) return;
    if (options.ellipsis) {
      const $texts = $yAxis.selectAll('.tick text');
      const position = {
        x: options.ellipsis.position && options.ellipsis.position.x ? options.ellipsis.position.x : 0
      };
      const ellipsisTextWidth = options.ellipsis.width - yAxis.tickPadding() - yAxis.tickSizeOuter() - labelOffsetX - labelHeight;
      $texts.style('text-anchor', 'end').call(ellipsisText, ellipsisTextWidth, position.x);
    }
    if (options.isYAxisHideDecimalValue) {
      $yAxis.selectAll('.tick').each(function (d, i) {
        if (Number(d) % 1 !== 0) {
          this.remove();
        }
      });
    }
    if (showBarGroupedLabel) {
      $yAxis.selectAll('.tick').each(function (d, i) {
        const $this = d3.select(this);
        const translateString = $this.attr('transform');
        const splitted = translateString.replace('translate(', '').replace(')', '').split(',');
        const tempX = Number(splitted[0]);
        const tempY = Number(splitted[1]);
        $this.attr('transform', `translate(${options.showBarGroupedTranslate.x}, ${tempY})`);
      });

      const $texts = $yAxis.selectAll('.tick text');
      $texts.style('fill', '#1890FF');

      $yAxis.selectAll('.tick line').each(function (d, i) {
        this.remove();
      });
    }
    return $yAxis;
  };
  const renderAxisLabel = $text => {
    if (!options.label) return;
    if ($text.empty()) return;
    const characterWidth = 3;
    const yAxisBBox = $text.node().parentElement.getBBox();
    let labelX = options.label.position && options.label.position.x
      ? options.label.position.x + (options.label.position.x < 0 ? labelHeight : -(labelHeight))
      : $container.node().getBBox().x - labelOffsetX;
    if (showBarGroupedLabel) {
      const calMarginLeft = (options.label.margin.left / 2) + 75;
      const marginYLabel = options.showBarGroupedTranslate.x > 0 ? calMarginLeft : -(calMarginLeft);
      labelX = options.showBarGroupedTranslate.x + marginYLabel;
    }
    const textWidth = options.label.text.length * characterWidth;
    $text
      .style('height', labelHeight)
      .attr('transform', 'rotate(-90)')
      .attr('x', -(yAxisBBox.height / 2) + textWidth)
      .attr('y', labelX);
    if (options.label.fontStyle) $text.style('font-family', options.label.fontStyle);
    if (options.label.fontSize) $text.style('font-size', options.label.fontSize);
    const wrapTextOptions = {};
    if (textWidth > (yAxisBBox.height / 2)) wrapTextOptions.x = 0;
    $text.text(options.label.text)
      .call(wrapText, yAxisBBox.height, wrapTextOptions);
  };
    // const yAxisLabel = $yAxis.selectAll(`.${Y_AXIS_LABEL_CLASSNAME}`).data([0]);
    // yAxisLabel
    //   .enter()
    //   .append('text')
    //   .attr('class', Y_AXIS_LABEL_CLASSNAME)
    //   .call(renderAxisLabel);
    // yAxisLabel
    //   .transition().duration(options.transitionDuration)
    //   .call(renderAxisLabel);
    // yAxisLabel
    //   .exit()
    //   .transition().duration(options.transitionDuration)
    //   .remove();

  /* Enter */
  joinedYAxis
    .enter()
    .append('g')
    .attr('class', Y_AXIS_CLASSNAME)
    .attr('transform', `translate(${_translate.x}, ${_translate.y})`)
    .call(yAxis)
    .call(adjustYAxis)
    .append('text')
    .attr('class', Y_AXIS_LABEL_CLASSNAME)
    .call(renderAxisLabel);
  /* Update */
  joinedYAxis
    .selectAll(`.${Y_AXIS_LABEL_CLASSNAME}`)
    .call(renderAxisLabel);
  joinedYAxis
    .transition().duration(options.transitionDuration)
    .attr('transform', `translate(${_translate.x}, ${_translate.y})`)
    .call(yAxis)
    .call(adjustYAxis);
  /* Exit */
  joinedYAxis
    .exit()
    .transition().duration(options.transitionDuration)
    .remove();

  const $yAxis = $container.select(`.${Y_AXIS_CLASSNAME}`);
  return $yAxis;
};

export const renderYGrid = ($container, yScale, options = {}) => {
  const $yGrid = $container.append('g')
    .attr('class', 'y-grid-line');

  const data = yScale.scaleType === 'scaleLinear' ? yScale.ticks() : yScale.domain();
  const $gridEnter = $yGrid.selectAll('.grid-line')
    .data(data)
    .enter();
  const $yAxis = $container.select(`.${Y_AXIS_CLASSNAME}`);
  const yAxisBBox = $yAxis.node().getBBox();
  const $yAxisLabel = $container.select(`.${Y_AXIS_LABEL_CLASSNAME}`);
  const posX = options.position && options.position.x !== undefined ? options.position.x : yAxisBBox.x;
  const x1 = posX === 0 || $yAxisLabel.empty() ? posX : posX + $yAxisLabel.node().getBBox().height;
  const $xAxis = $container.select(`.${X_AXIS_CLASSNAME}`);
  const posX2 = options.position && options.position.x2 !== undefined ? options.position.x2 : undefined;
  const xAxisBBox = $xAxis.empty() ? null : $xAxis.node().getBBox();
  let x2 = xAxisBBox ? xAxisBBox.width : 0;
  if (posX2) {
    x2 = posX2;
  }

  $gridEnter.append('line')
    .attr('class', 'grid')
    .attr('transform', d => `translate(0, ${yScale(d)})`)
    .attr('x1', x1)
    .attr('x2', x2);

  if (options.isYAxisHideDecimalValue) {
    $gridEnter.selectAll('.grid').each(function (d, i) {
      if (d === 0 || Number(d) % 1 !== 0) {
        this.remove();
      }
    });
  }

  // Line equal X Axis line
  // hGridContainer
  //   .append('g')
  //   .attr('class', 'h-grid')
  //   .attr('transform', () => {
  //     return 'translate(0, ' + this.height_ + ')';
  //   })
  //   .append('line')
  //   .attr('x1', () => {
  //     return -this.margin.left;
  //   })
  //   .attr('x2', () => {
  //     return x(x.domain()[1]);
  //   });

  return $yGrid;
};

export const hideYAxisDomain = ($container) => {
  $container.selectAll('.y-axis .domain').each(function (d, i) {
    this.remove();
  });
};

const YAxis = {
  Y_AXIS_CLASSNAME: Y_AXIS_CLASSNAME,
  Y_AXIS_LABEL_CLASSNAME: Y_AXIS_LABEL_CLASSNAME,
  getYScale: getYScale,
  getYAxis: getYAxis,
  renderYAxis: renderYAxis,
  renderYGrid: renderYGrid,
  hideYAxisDomain: hideYAxisDomain
};

export default YAxis;
